import {
  Alert,
  Badge,
  Card,
  Center,
  Group,
  Loader,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { IconAlertTriangle, IconSeeding } from '@tabler/icons-react';
import { Fragment, useEffect, useState } from 'react';
import { CohortStrip } from '@/components/cohort-strip';
import { FunnelShape, type FunnelBand } from '@/components/funnel-shape';
import { PageHeader } from '@/components/page-header';
import { SectionHeading } from '@/components/section-heading';
import { Sparkline } from '@/components/sparkline';
import { StatTile } from '@/components/stat-tile';
import { api } from '@/lib/api-client';
import { ApiError, NetworkError } from '@/lib/api-error';
import {
  FUNNEL_STEPS,
  OFFSETS,
  type CohortFunnel,
  type FunnelResponse,
  type FunnelStepId,
  type OffsetKey,
} from '@/lib/analytics';
import classes from './analytics-page.module.css';

const format = new Intl.NumberFormat('en-GB');

/**
 * How registration was reached, as the API splits it.
 *
 * "Was required to" is a measured zero rather than an absence: the rung is
 * built, and nothing in the product requires an account yet. The row is here so
 * that the day one does, the number appears without a screen change.
 */
const TRIGGERS = [
  { key: 'chose', label: 'Chose to' },
  { key: 'required', label: 'Was required to' },
] as const;

function step(id: FunnelStepId) {
  return FUNNEL_STEPS.find((entry) => entry.id === id);
}

function describeError(error: unknown): string {
  if (error instanceof NetworkError) {
    return 'The API did not answer. It may not be running.';
  }
  if (error instanceof ApiError) return error.message;
  return 'Something went wrong loading the funnel.';
}

export function FunnelPage() {
  const [data, setData] = useState<FunnelResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cohortId, setCohortId] = useState<string | null>(null);
  const [offset, setOffset] = useState<OffsetKey>('d28');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const response = await api.get<FunnelResponse>('/admin/analytics/funnel');
        if (cancelled) return;
        setData(response);
        setError(null);
      } catch (caught) {
        if (!cancelled) setError(caught);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading) {
    return (
      <Stack gap="lg" className={classes.page}>
        <PageHeader title="Funnel" />
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack gap="lg" className={classes.page}>
        <PageHeader title="Funnel" />
        <Alert
          color="red"
          title="Cannot load the funnel"
          icon={<IconAlertTriangle size={18} />}
        >
          <Text size="sm">{describeError(error)}</Text>
        </Alert>
      </Stack>
    );
  }

  const cohorts = data?.cohorts ?? [];
  const selected: CohortFunnel | undefined =
    cohorts.find((entry) => entry.id === cohortId) ?? cohorts[0];

  if (!selected) {
    return (
      <Stack gap="lg" className={classes.page}>
        <PageHeader
          title="Funnel"
          description="Where users stop, by the week their first run fell in."
        />
        <Card>
          <Stack gap="xs" align="center" py="xl">
            <IconSeeding size={28} stroke={1.4} />
            <Text fw={600}>No first runs yet</Text>
            <Text size="sm" c="dimmed" ta="center" maw={440}>
              Nothing has opened the app and reached the API. The first user
              to launch it creates a cohort here. Everything on this screen is
              measured — there is no sample data behind it.
            </Text>
          </Stack>
        </Card>
      </Stack>
    );
  }

  const firstRunRung = selected.rungs.find((rung) => rung.step === 'first-run');
  const firstRun = firstRunRung?.counts?.[offset] ?? null;
  const measured = selected.rungs.filter(
    (rung) => rung.status === 'measured',
  ).length;

  const bands: FunnelBand[] = selected.rungs.map((rung) => ({
    id: rung.step,
    label: step(rung.step)?.label ?? rung.step,
    description: step(rung.step)?.description ?? '',
    count: rung.counts?.[offset] ?? null,
    absence: rung.status === 'not-built' ? ('not-built' as const) : undefined,
    aside:
      rung.status === 'measured' ? (
        <Sparkline
          values={[...cohorts]
            .reverse()
            .map(
              (entry) =>
                entry.rungs.find((r) => r.step === rung.step)?.counts?.[
                  offset
                ] ?? null,
            )}
          label={`${step(rung.step)?.label ?? rung.step} across every cohort`}
        />
      ) : undefined,
  }));

  return (
    <Stack gap="xl" className={classes.page}>
      <PageHeader
        title="Funnel"
        description="Where users stop, by the week their first run fell in."
        actions={
          <Group gap="sm" align="flex-end" wrap="wrap">
            <Select
              label="Cohort"
              size="sm"
              w={190}
              allowDeselect={false}
              value={selected.id}
              onChange={(value) => value && setCohortId(value)}
              data={cohorts.map((entry) => ({
                value: entry.id,
                label: entry.label,
              }))}
            />
            <Stack gap={4}>
              <Text size="sm" fw={500}>
                Offset
              </Text>
              <SegmentedControl
                size="sm"
                value={offset}
                onChange={(value) => setOffset(value as OffsetKey)}
                data={OFFSETS.map((entry) => ({
                  value: entry.key,
                  label: entry.label,
                }))}
              />
            </Stack>
          </Group>
        }
      />

      <Card>
        <div className={classes.hero}>
          <StatTile
            variant="plain"
            size="lg"
            label="First runs"
            value={firstRun == null ? '—' : format.format(firstRun)}
            secondary={selected.label}
            note="Devices that opened the app and reached the API. A reinstall is a new first run, so this counts devices starting out, not people — and the API never sees an install, only a first server call."
          />
          <div className={classes.heroDivider} />
          <StatTile
            variant="plain"
            label="Cohorts"
            value={String(cohorts.length)}
            secondary="Weeks with at least one first run"
          />
          <StatTile
            variant="plain"
            label="Rungs measured"
            value={`${measured} of ${selected.rungs.length}`}
            secondary="The rest wait on a payment provider"
            note="A rung reports as not built rather than as zero. Zero is a measurement; nothing recording it is not, and the two must not read alike. The two unbuilt rungs are subscription and first payment, and whether the last one exists at all depends on pricing carrying a trial."
          />
          <StatTile
            variant="plain"
            label="Cohort state"
            value={selected.inProgress ? 'Open' : 'Closed'}
            secondary={
              selected.inProgress
                ? 'The week has not ended — still accumulating'
                : 'The week has closed'
            }
          />
        </div>
      </Card>

      <Stack gap="sm">
        <SectionHeading
          eyebrow="Every cohort"
          title="Shape by week"
          subtitle="Pick one. Grey bands are rungs nothing measures yet."
          note="Cumulative-since-launch funnels are not shown anywhere: they only ever improve, they flatter old cohorts for no reason but age, and they cannot be compared to anything."
        />
        <Card>
          <CohortStrip
            selectedId={selected.id}
            onSelect={setCohortId}
            items={cohorts.map((entry) => ({
              id: entry.id,
              label: entry.label.replace('Week of ', ''),
              values: entry.rungs.map((rung) => rung.counts?.[offset] ?? null),
            }))}
          />
        </Card>
      </Stack>

      <div className={classes.split}>
        <Stack gap="sm">
          <SectionHeading
            eyebrow="Rungs 1–4"
            title="First-run clock"
            subtitle={
              OFFSETS.find((entry) => entry.key === offset)?.description
            }
            note="Each step strictly contains the next: nobody is subscribed without an account, nobody has an account without having finished the assessment. That containment is what makes the percentages mean anything, and any new rung has to preserve it."
          />
          <Card>
            <FunnelShape bands={bands.slice(0, 4)} />
          </Card>
        </Stack>

        <Stack gap="sm">
          <SectionHeading
            eyebrow="Rung 5"
            title="Trial clock"
            actions={
              <Badge variant="light" color="magenta">
                Other clock
              </Badge>
            }
            note="Measured from the day each trial started, not from first run. On a first-run clock nobody reaches it inside the first week, so the column would read zero and look like an outage."
          />
          <Card>
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                First payment taken
              </Text>
              <Text size="sm" c="dimmed">
                Not built. There is no subscription model, and pricing has not
                settled whether there is a trial for this rung to measure.
              </Text>
            </Stack>
          </Card>

          <Card>
            <Stack gap={6}>
              <Text size="xs" fw={600} c="dimmed">
                MEASURED
              </Text>
              <Text size="sm">
                {data?.generatedAt
                  ? new Date(data.generatedAt).toLocaleString('en-GB')
                  : '—'}
              </Text>
            </Stack>
          </Card>
        </Stack>
      </div>

      <Stack gap="sm">
        <SectionHeading
          eyebrow="Detail"
          title="Every offset"
          subtitle="A user reaches the first rung at t=0, so no offset excludes anyone in the cohort."
          note="Sign-in comes after the value, which makes first-run-to-registration look worse and first-run-to-second-session look better. That trade was made on purpose in docs/onboarding.md. Read this rung with that in mind: it can always be improved by asking for an account earlier, and doing so would break the product the funnel exists to measure."
        />
        <Card p={0}>
          <Table.ScrollContainer minWidth={480}>
            <Table verticalSpacing="sm" horizontalSpacing="lg">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Step</Table.Th>
                  {OFFSETS.map((entry) => (
                    <Table.Th key={entry.key} className={classes.numeric}>
                      {entry.label}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {selected.rungs.map((rung) => (
                  <Fragment key={rung.step}>
                    <Table.Tr>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          <Text size="sm" fw={600}>
                            {step(rung.step)?.label ?? rung.step}
                          </Text>
                          {rung.status === 'not-built' && (
                            <Badge variant="light" color="gray" size="xs">
                              Not built
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      {OFFSETS.map((entry) => {
                        const count = rung.counts?.[entry.key] ?? null;
                        return (
                          <Table.Td key={entry.key} className={classes.numeric}>
                            <Text
                              size="sm"
                              c={count == null ? 'dimmed' : undefined}
                            >
                              {count == null ? '—' : format.format(count)}
                            </Text>
                          </Table.Td>
                        );
                      })}
                    </Table.Tr>

                    {/* The trigger split, in the table only. It belongs nowhere
                        near the funnel shape: the two triggers do not contain
                        one another, and drawing them as bands would read as two
                        more steps. */}
                    {rung.breakdown &&
                      TRIGGERS.map(({ key, label }) => (
                        <Table.Tr key={`${rung.step}-${key}`}>
                          <Table.Td className={classes.splitRowLabel}>
                            <Text size="sm" c="dimmed">
                              {label}
                            </Text>
                          </Table.Td>
                          {OFFSETS.map((entry) => {
                            const count = rung.breakdown?.[key][entry.key];
                            return (
                              <Table.Td
                                key={entry.key}
                                className={classes.numeric}
                              >
                                <Text size="sm" c="dimmed">
                                  {count == null ? '—' : format.format(count)}
                                </Text>
                              </Table.Td>
                            );
                          })}
                        </Table.Tr>
                      ))}
                  </Fragment>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      </Stack>
    </Stack>
  );
}
